import '../icon/index.js';

import {html} from 'lit';
import {customElement, property} from 'lit/decorators.js';
import {FlywheelComponent} from '#lit/lib/component.js';
import styles from './style.scss';
import {classMap} from 'lit/directives/class-map.js';
import type {StyleInfo} from 'lit/directives/style-map.js';
import {styleMap} from 'lit/directives/style-map.js';
import {getValueByHash} from '#lit/utils/arrays.js';

export const AVATAR_ELEMENT_TAG = 'fly-avatar';

/**
 * The default size for an avatar.
 */
export const DEFAULT_SIZE = 30;

export enum AvatarColorVariants {
  PRIMARY = 'primary',
  SECONDARY = 'secondary',
  GREEN = 'green',
  YELLOW = 'yellow',
  RED = 'red',
  PRIMARY_DARK = 'primary--dark',
  SECONDARY_DARK = 'secondary--dark',
  GREEN_DARK = 'green--dark',
  YELLOW_DARK = 'yellow--dark',
  RED_DARK = 'red--dark',
}

/**
 * Avatar -- Description here
 */
@customElement(AVATAR_ELEMENT_TAG)
export default class Avatar extends FlywheelComponent {
  @property()
  name = '';

  @property()
  icon = '';

  @property()
  image = '';

  @property()
  profileURL = '';

  @property({type: Number})
  size: number = DEFAULT_SIZE;

  @property()
  variant: AvatarColorVariants = AvatarColorVariants.SECONDARY;

  @property({type: Boolean})
  circle = false;

  @property({type: Boolean})
  randomize = false;

  @property({type: Boolean})
  disableClick = false;

  static get styles() {
    return [styles];
  }

  get classes(): unknown {
    if (this.randomize) {
      this.variant = getValueByHash<AvatarColorVariants>(
        Object.values(AvatarColorVariants),
        this.name
      );
    }

    return classMap({
      'fly-avatar--link': this.profileURL,
      'fly-avatar--circle': this.circle,
      'fly-avatar--primary': this.variant === AvatarColorVariants.PRIMARY,
      'fly-avatar--secondary': this.variant === AvatarColorVariants.SECONDARY,
      'fly-avatar--green': this.variant === AvatarColorVariants.GREEN,
      'fly-avatar--yellow': this.variant === AvatarColorVariants.YELLOW,
      'fly-avatar--red': this.variant === AvatarColorVariants.RED,
      'fly-avatar--primary--dark':
        this.variant === AvatarColorVariants.PRIMARY_DARK,
      'fly-avatar--secondary--dark':
        this.variant === AvatarColorVariants.SECONDARY_DARK,
      'fly-avatar--green--dark':
        this.variant === AvatarColorVariants.GREEN_DARK,
      'fly-avatar--yellow--dark':
        this.variant === AvatarColorVariants.YELLOW_DARK,
      'fly-avatar--red--dark': this.variant === AvatarColorVariants.RED_DARK,
    });
  }

  clickHandler(e: MouseEvent) {
    if (this.profileURL) {
      e.stopPropagation();
      location.href = this.profileURL;
    }
  }

  get backgroundImage(): StyleInfo {
    return {
      'background-image': this.image ? `url('${this.image}')` : 'unset',
      'background-size': 'cover',
      width: this.size ? `${this.size}px` : 'inherit',
      height: this.size ? `${this.size}px` : 'inherit',
      '--fly-icon-size': this.size ? `${this.size / 1.9}px` : '1rem',
    };
  }

  get sizes(): StyleInfo {
    if (!this.size) return {};
    return {
      'font-size': `${this.size / 1.72}px`,
    };
  }

  render() {
    return html`
      <div
        class="fly-avatar ${this.classes}"
        @click="${(e: MouseEvent) => this.clickHandler(e)}"
        style="${styleMap({...this.backgroundImage, ...this.sizes})}"
        title="${this.name}"
      >
        ${this.icon ? html`<fly-icon icon=${this.icon}></fly-icon>` : ''}
        ${!this.image && !this.icon ? this.name.substring(0, 1) : ''}
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'fly-avatar': Avatar;
  }
}
